import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Mic, MicOff, Send, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { toast } from "sonner";

export default function VoiceChatPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [recording, setRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await api.get("/chat/history");
      setMessages(res.data.messages);
    } catch {
      // ignore
    }
  };

  const sendTextMessage = async () => {
    if (!textInput.trim() || sending) return;
    const msg = textInput.trim();
    setTextInput("");
    setMessages(prev => [...prev, { role: "user", content: msg, created_at: new Date().toISOString() }]);
    setSending(true);
    try {
      const res = await api.post("/chat/message", { message: msg });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response, created_at: new Date().toISOString() }]);
    } catch {
      toast.error("Failed to get response");
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await sendVoiceMessage(blob);
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const sendVoiceMessage = async (blob) => {
    setSending(true);
    setMessages(prev => [...prev, { role: "user", content: "Voice message...", type: "voice", created_at: new Date().toISOString() }]);
    try {
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");
      const res = await api.post("/chat/voice", formData, { headers: { "Content-Type": "multipart/form-data" } });

      // Update the last user message with transcription
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1].content = res.data.user_text;
        return [...updated, {
          role: "assistant",
          content: res.data.response,
          audio_base64: res.data.audio_base64,
          created_at: new Date().toISOString()
        }];
      });
    } catch {
      toast.error("Voice chat failed");
    } finally {
      setSending(false);
    }
  };

  const playAudio = (audioBase64, msgIndex) => {
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(`data:audio/mpeg;base64,${audioBase64}`);
    audio.onplay = () => setPlayingId(msgIndex);
    audio.onended = () => setPlayingId(null);
    audio.play();
    audioRef.current = audio;
  };

  return (
    <div className="flex flex-col h-full" data-testid="voice-chat-page">
      {/* Header */}
      <div className="p-6 md:px-12 border-b border-white/[0.06]">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-1 font-medium">AI Companion</p>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Voice Chat</h1>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-6 md:px-12">
        <div className="max-w-3xl mx-auto space-y-4 pb-4">
          {messages.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-[#00FF88]/10 flex items-center justify-center mx-auto mb-6">
                <Mic className="w-8 h-8 text-[#00FF88]" />
              </div>
              <p className="text-lg text-white mb-2" style={{ fontFamily: 'Manrope' }}>Start a conversation</p>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                Type a message or hold the microphone button to speak. Your AI companion remembers every conversation.
              </p>
            </div>
          )}
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-5 py-3 ${
                  msg.role === "user"
                    ? "bg-white/10 text-white"
                    : "bg-white/[0.03] backdrop-blur-xl border border-[#00FF88]/10 text-slate-200"
                }`}
                data-testid={`chat-message-${i}`}
              >
                <p className="text-sm leading-relaxed">{msg.content}</p>
                {msg.audio_base64 && (
                  <button
                    onClick={() => playAudio(msg.audio_base64, i)}
                    className="mt-2 flex items-center gap-2 text-xs text-[#00FF88] hover:underline"
                    data-testid={`play-audio-${i}`}
                  >
                    <Volume2 className={`w-3 h-3 ${playingId === i ? "animate-pulse" : ""}`} />
                    {playingId === i ? "Playing..." : "Play audio"}
                  </button>
                )}
                <p className="text-[10px] text-slate-600 mt-1">
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </motion.div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white/[0.03] backdrop-blur-xl border border-[#00FF88]/10 rounded-2xl px-5 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-[#00FF88] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-[#00FF88] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-[#00FF88] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-4 md:px-12 border-t border-white/[0.06] bg-black">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="flex-1 relative">
            <Input
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTextMessage(); } }}
              placeholder="Type a message..."
              data-testid="chat-text-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-full h-12 pr-12"
              disabled={sending}
            />
            <button
              onClick={sendTextMessage}
              disabled={!textInput.trim() || sending}
              data-testid="chat-send-btn"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 disabled:opacity-30 transition-colors duration-200"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
          <button
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onMouseLeave={() => { if (recording) stopRecording(); }}
            onTouchStart={startRecording}
            onTouchEnd={stopRecording}
            disabled={sending}
            data-testid="chat-voice-btn"
            className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-colors duration-200 ${
              recording
                ? "bg-red-500 text-white"
                : "bg-[#00FF88] text-black hover:bg-[#00CC6A]"
            }`}
          >
            {recording ? (
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-red-500 pulse-ring" />
                <MicOff className="w-5 h-5 relative z-10" />
              </div>
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
